import { connect, injectReducer, injectSaga } from '@vfde-sails/core';
import { mountLoadingSpinner, updateLoadingSpinner } from '../../components/loadingSpinner';
import { AppContainerId, CustomerInfoId, NameSpace, ProductType } from '../../enum';
import * as actionMap from './action';
import appReducer from './reducer';
import { mapStateToProps } from './selector';
import { getUpdateFlags } from './update';
import appSaga from './saga';
import productContainer from '../Product';
import addressValidationContainer from '../AddressValidation';
import { mountOverlay, showOverlay, updateOverlayView } from '../../components/overlay';
import { mountOptionPicker, updateGigakombiPicker } from '../../components/optionPicker';
import { initFootnotes } from '../../components/footnote';
import { mountAccordion, updateFootnoteAccordion } from '../../components/accordion';
import { mountCarousel } from '../../components/carousel';
import { mountTabs, updateActiveDeviceTab } from '../../components/tabs';
import { mountRelatedLinks } from '../../components/relatedLinks';
import { toggleElementVisibilityById } from '@vfde-brix/ws10/core';
import { TrackingUIType } from '@vfde-sails/tracking';
import { getCMSConfig } from '../../util/cms';
import { LoadingSpinner } from '@vfde-brix/ws10/loading-spinner';
import { mountJustText, updateJustText } from '../../components/justText';
import justTextUtil from '../../util/component/justText.utils';
import {
    AVAILABILITY_NAMESPACE,
    availabilityContainer,
    availabilitySaga,
    availabiltyReducer,
} from '@vfde-sails/availability';
import { LoginNameSpace, loginReducer, loginSaga } from '@vfde-sails/login';

export type StateProps = ReturnType<typeof mapStateToProps>;
export type Actions = typeof actionMap;

function appContainer(state: StateProps, actions: Actions) {
    injectSaga(NameSpace.APP, appSaga);
    injectReducer(NameSpace.APP, appReducer);
    injectSaga(AVAILABILITY_NAMESPACE, availabilitySaga);
    injectReducer(AVAILABILITY_NAMESPACE, availabiltyReducer);
    injectSaga(LoginNameSpace.App, loginSaga);
    injectReducer(LoginNameSpace.App, loginReducer);

    productContainer();

    let overlaySpinner: LoadingSpinner | null;

    const appSpinner = mountLoadingSpinner(AppContainerId.APP_LOADING);
    const overlay = mountOverlay(AppContainerId.OVERLAY, actions.setOverlayVisibility);
    const accordion = mountAccordion(AppContainerId.FOOTNOTE_ACCORDION, TrackingUIType.Footnote);
    const gigakombiPicker = mountOptionPicker(AppContainerId.GIGAKOMBI_PICKER, actions.setSelectedGigakombi);
    const customerName = mountJustText(CustomerInfoId.CUSTOMER_NAME);
    const customerCity = mountJustText(CustomerInfoId.CUSTOMER_CITY);
    const customerStreet = mountJustText(CustomerInfoId.CUSTOMER_STREET);
    const customerId = mountJustText(CustomerInfoId.CUSTOMER_NUMBER);

    const cmsProps = getCMSConfig();

    mountTabs(AppContainerId.TV_TABS);
    mountTabs(AppContainerId.DEVICE_TABS);
    mountCarousel(AppContainerId.HIGHLIGHT_CAROUSEL);
    mountCarousel(AppContainerId.DEVICE_HOMEBOX_CAROUSEL);
    mountCarousel(AppContainerId.DEVICE_SOUNDBOX_CAROUSEL);
    mountAccordion(AppContainerId.FAQ_ACCORDION, TrackingUIType.FAQ);
    mountAccordion(AppContainerId.LEGAL_ACCORDION, TrackingUIType.Accordion);
    mountRelatedLinks(AppContainerId.RELATED_OFFERS);
    mountRelatedLinks(AppContainerId.RELATED_LINKS);

    initFootnotes(accordion);

    actions.initApp(overlay);

    return {
        getDerivedStateFromProps(newProps: StateProps, oldProps: StateProps) {
            const update = getUpdateFlags(newProps, oldProps);
            const {
                isError,
                tvConnect,
                tvConnectBv,
                isAppLoading,
                isOverlayLoading,
                selected,
                isGigakombi,
                gigakombi,
                customer,
            } = newProps;
            const isNet = selected.type === ProductType.NET;

            if (update.error) {
                toggleElementVisibilityById(AppContainerId.APP, !isError);
                toggleElementVisibilityById(AppContainerId.ERROR, isError);

                return;
            }

            if (update.overlayLoaded) {
                addressValidationContainer();
                availabilityContainer();

                overlaySpinner = mountLoadingSpinner(AppContainerId.OVERLAY_LOADING);
            }

            if (update.gigakombi) {
                updateGigakombiPicker(gigakombiPicker, gigakombi, customer);
                toggleElementVisibilityById(AppContainerId.GIGAKOMBI_INFORMATION, isGigakombi && !customer);
            }

            if (update.customer) {
                toggleElementVisibilityById(CustomerInfoId.WRAPPER, customer?.hasCableContract);
                updateJustText(customerName, justTextUtil.getCustomerName(customer?.cableContract));
                updateJustText(customerCity, justTextUtil.getCustomerCity(customer?.cableContract));
                updateJustText(customerStreet, justTextUtil.getCustomerStreet(customer?.cableContract?.address));
                updateJustText(customerId, customer?.cableContract?.accountId);
            }

            update.appSpinner && updateLoadingSpinner(appSpinner, isAppLoading);
            update.overlaySpinner && updateLoadingSpinner(overlaySpinner, isOverlayLoading);
            update.overlayVisibility && showOverlay(overlay, newProps.overlay.isOpen);
            update.overlayView && updateOverlayView(newProps.overlay.view);
            update.footnotes && initFootnotes(accordion);
            update.footnoteAccordion && updateFootnoteAccordion(accordion, cmsProps, tvConnect, tvConnectBv);
            update.deviceTabs && updateActiveDeviceTab(selected.device);
            update.qualitySeal && toggleElementVisibilityById(AppContainerId.QUALITY_SEAL, isNet);
            update.gigakombiContainer && toggleElementVisibilityById(AppContainerId.GIGAKOMBI_CONTAINER, !isNet);
        },
    };
}

export default connect(mapStateToProps, actionMap)(appContainer);
